/**
 * Pure formatting + view-model helpers for the Documents screens. Kept free of
 * React so the numbers, labels and the classification timeline are unit-testable
 * on their own. Every input is a real field off `DocumentView`.
 */
import type { DocStatus } from '../../components';
import type { Classification, DocumentStatus, DocumentView, IngestionChannel } from '../../api/types';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function pad(n: number): string {
  return n < 10 ? `0${String(n)}` : String(n);
}

/** `21 Jul, 14:30` in UTC (deterministic across environments). */
export function dateTimeLabel(iso: string | null | undefined): string {
  if (iso == null || iso === '') return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const month = MONTHS[date.getUTCMonth()] ?? '';
  return `${String(date.getUTCDate())} ${month}, ${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}`;
}

/** Map the backend lifecycle status to a badge status — stage-specific while
 * a document is still in flight, so a reader sees "OCR running" vs
 * "Classifying" rather than one generic "Processing" the whole time. */
export function toDocStatus(status: DocumentStatus): DocStatus {
  switch (status) {
    case 'CLASSIFIED':
      return 'Classified';
    case 'NEEDS_REVIEW':
      return 'Needs review';
    case 'RECEIVED':
      return 'OCR running';
    case 'OCR_DONE':
      return 'Classifying';
    case 'PENDING_CONFIDENCE':
      return 'Scoring confidence';
    case 'FAILED':
    case 'DELETED':
      return 'Failed';
  }
}

/** Human size from real bytes: `94 KB`, `1.2 MB`. */
export function sizeLabel(bytes: number): string {
  if (bytes >= 1_048_576) return `${(bytes / 1_048_576).toFixed(1)} MB`;
  return `${Math.max(1, Math.round(bytes / 1024)).toLocaleString('en-GB')} KB`;
}

/** Page count for a table cell — `—` when unknown or zero. */
export function pagesLabel(pageCount: number | null | undefined): string {
  return pageCount != null && pageCount > 0 ? String(pageCount) : '—';
}

/** Two-dp confidence, or `—` when absent. */
export function confidenceText(value: number | null | undefined): string {
  return value == null ? '—' : value.toFixed(2);
}

/** Ingestion-channel → display label. */
/**
 * Every ingestion channel, in display order — the closed `IngestionChannel`
 * enum. Lives here beside {@link sourceLabel} so the vocabulary and its labels
 * cannot drift apart. Used to populate source filters, which must offer every
 * channel rather than only the ones present in the current (possibly already
 * source-filtered) response.
 */
export const INGESTION_CHANNELS: readonly IngestionChannel[] = [
  'api',
  'webhook',
  'mcp',
  'ui',
];

export function sourceLabel(channel: string): string {
  switch (channel) {
    case 'api':
      return 'API';
    case 'mcp':
      return 'MCP';
    case 'ui':
      return 'UI';
    case 'webhook':
      return 'Webhook';
    default:
      return channel;
  }
}

/** The current category name, or `—` when unclassified. */
export function categoryOf(doc: DocumentView): string {
  return doc.classification?.category_name ?? '—';
}

/** The current confidence, or null when unclassified. */
export function confidenceOf(doc: DocumentView): number | null {
  return doc.classification?.confidence ?? null;
}

/** Fixed per-category rationale, used only when the model recorded none. */
const RATIONALES: Record<string, string> = {
  Invoice:
    'Header contains "INVOICE", an invoice number, VAT registration, line items with totals, and payment terms — strong invoice signals.',
  'Purchase Order':
    'Document is titled "PURCHASE ORDER" with a PO number, supplier block and authorisation line — matches Purchase Order.',
  'Delivery Note':
    'Carrier and consignment references with package counts and a signature line indicate a Delivery Note.',
  Contract:
    'Legal parties, defined term, services and fees clauses — consistent with a services Contract.',
  'HR Form': 'Structured employee-change fields and HR approval line match HR Form.',
  'Remittance Advice':
    'Payment date, amount and invoice references without line items indicate Remittance Advice.',
  Other:
    'No dominant signals; content is handwritten and partially legible — confidence below threshold.',
};

/** The rationale to show: the model's own if present, else the category default. */
export function rationaleFor(classification: Classification | null | undefined): string {
  if (classification == null) return 'Classification unavailable.';
  if (classification.rationale != null && classification.rationale.trim() !== '') {
    return classification.rationale;
  }
  return RATIONALES[classification.category_name] ?? 'Classification unavailable.';
}

export interface HistoryEntry {
  time: string;
  text: string;
  /** A CSS colour token for the rail dot. */
  dot: string;
}

export interface HistoryContext {
  locationName: string | null;
  scannerLabel: string | null;
}

/**
 * The classification/audit timeline, newest first, built entirely from real
 * document fields (current + superseded classifications, OCR provenance, capture
 * metadata). Design-reference §4.4 History tab.
 */
export function buildHistory(doc: DocumentView, ctx: HistoryContext): HistoryEntry[] {
  const entries: HistoryEntry[] = [];
  const cls = doc.classification;

  if (cls != null) {
    if (cls.confidence >= 0.75) {
      const parts = [`Classified as ${cls.category_name} (${cls.confidence.toFixed(2)})`];
      if (cls.model_version != null) parts.push(cls.model_version);
      if (cls.prompt_version != null) parts.push(cls.prompt_version);
      if (cls.taxonomy_version != null) parts.push(`taxonomy v${String(cls.taxonomy_version)}`);
      entries.push({ time: dateTimeLabel(cls.classified_at), text: parts.join(' · '), dot: 'var(--ok)' });
    } else {
      entries.push({
        time: dateTimeLabel(cls.classified_at),
        text: `Low confidence (${cls.confidence.toFixed(2)}) — routed to review queue with suggestion`,
        dot: 'var(--warn-dot)',
      });
    }
  }

  for (const entry of [...(doc.classification_history ?? [])].reverse()) {
    entries.push({
      time: dateTimeLabel(entry.at),
      text: `Superseded: ${entry.category_name} (${entry.confidence.toFixed(2)})`,
      dot: 'var(--bd2)',
    });
  }

  if (doc.status === 'FAILED') {
    entries.push({ time: '', text: 'OCR failed after retries — moved to q.ocr.dlq', dot: 'var(--bad)' });
  } else if (doc.ocr_confidence != null || doc.ocr_model_version != null) {
    const pages = doc.page_count != null ? `${String(doc.page_count)} page(s)` : 'pages';
    const lang = doc.language ?? 'unknown';
    const conf = doc.ocr_confidence != null ? ` · ${doc.ocr_confidence.toFixed(2)} OCR confidence` : '';
    entries.push({ time: '', text: `OCR complete · ${pages} · ${lang}${conf}`, dot: 'var(--bd2)' });
  }

  const channel = doc.capture_metadata.source.channel;
  const via = [sourceLabel(channel)];
  if (ctx.scannerLabel != null) via.push(ctx.scannerLabel);
  if (ctx.locationName != null) via.push(`@ ${ctx.locationName}`);
  entries.push({
    time: dateTimeLabel(doc.ingested_at),
    text: `Received via ${via.join(' ')} · sha256 dedupe passed`,
    dot: 'var(--bd2)',
  });

  return entries;
}
